import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Grid, styled } from "@mui/material";

import { Message, MessageSections } from "../../pages/components/message/message";
import { OnlineStoresModel } from "../../pages/shared/models/onlineStoresModel";

import OnlineStore from "../home/components/OnlineStores/OnlineStore";
import { Title } from "../home/components/Title/Title";
import Container from "../home/components/Container";
import Layout from "../home/components/Layout";

import onlineStoryCategoryService from "../../services/online-story-category-service";
import onlineStoresService from "../../services/online-stores-service";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getCountries } from "../../redux/slices/static-data.slice";

import Filters from "./Filters/Filters";

type StoresProps = {
    [key: string]: OnlineStoresModel[];
};

export const CategoryWrapper = styled(Grid)`
  display: flex;
  align-items: start;
  justify-content: space-between;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 0;
  }
`;

export const StoreWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
`;

export const FilterWraper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 18px;
  width: 100%;
  flex-wrap: wrap;
`;

const OnlineStores: React.FC = () => {
    const { languageId } = useAppSelector(state => state.messagesReducer);
    const [stores, setStores] = useState<StoresProps[]>([]);
    const [ready, setReady] = useState<boolean>(false);
    const { countries } = useAppSelector(state => state.staticDataReducer);
    const [searchParams] = useSearchParams();
    const country = searchParams.get('country');
    const dispatch = useAppDispatch();


    const filterByCountry = (data: StoresProps[], countryId: string | null): StoresProps[] => {
        // Get all country IDs that have active warehouses
        const activeCountryIds = countries
            .filter(country => country.hasActiveWarehouse)
            .map(country => country.countryId.toString());

        return data
            .map(category => {
                const categoryName = Object.keys(category)[0];
                let filteredStores = category[categoryName];
                
                // If a specific country is selected from URL, filter by that country
                if (countryId) {
                    filteredStores = filteredStores.filter(store => store.country === countryId);
                } else {
                    // Otherwise, filter by all countries with active warehouses
                    filteredStores = filteredStores.filter(store => 
                        activeCountryIds.includes(store.country)
                    );
                }
                
                return filteredStores.length > 0 ? { [categoryName]: filteredStores } : null;
            })
            .filter(Boolean) as StoresProps[];
    };

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const categoriesResponse = await onlineStoryCategoryService.getOnlineStoreCategories();
                if (categoriesResponse?.data?.length) {
                    const storesPromises = categoriesResponse.data.map(async (category) => {
                        if (category?.id) {
                            const storesResponse = await onlineStoresService.getStoreByCategoryId(category.id);
                            if (storesResponse?.data?.length) {
                                return { [category.name[languageId]]: storesResponse.data };
                            }
                        }
                        return null;
                    });
                    const resolvedStores = (await Promise.all(storesPromises)).filter(Boolean) as StoresProps[];
                    setStores(filterByCountry(resolvedStores, country));
                }
            } catch (error) {
                console.error("Error fetching stores:", error);
            } finally {
                setReady(true);
            }
        };

        fetchStores();
    }, [languageId, country, countries]);

    useEffect(() => {
        dispatch(getCountries(true))
    }, []);


    const allFilter = {
        countryId: 0,
        name: 'All',
        abbreviation: 'all',
        hasActiveWarehouse: true,
        isAllowedExport: false,
        warehouse: [],
    }

    return (
        <Layout>
            <Container>
                <Title variant="h2">
                    <Message
                        messageKey={"online_stores_title"}
                        sectionId={MessageSections.MAIN}
                    />
                </Title>
                <FilterWraper>
                    <Filters {...allFilter} />
                    {countries
                        .filter(country => country.hasActiveWarehouse)
                        .map((country) => (
                            <Filters {...country} key={country.abbreviation} />
                        ))}
                </FilterWraper>
                <CategoryWrapper container sx={{ marginBottom: "60px" }} justifyContent="center">
                    {ready &&
                        stores.map((category, index) => {
                            const [categoryName, items] = Object.entries(category)[0];
                            return (
                                <div key={index} style={{ marginBottom: "20px" }}>
                                    <h2>{categoryName.toUpperCase()}</h2>
                                    <StoreWrapper>
                                        {items.map((item) => (
                                            <OnlineStore {...item} key={item.id} />
                                        ))}
                                    </StoreWrapper>
                                </div>
                            );
                        })}
                </CategoryWrapper>
            </Container>
        </Layout>
    );
};

export default OnlineStores;